import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getActiveCustomerCart } from "../utils/getCart"

const commerceApi = CommerceService.api()

/**
 * Retrieves all user created carts as an array for both IDM and ADM users
 */
export const queryUserCarts = {
  async userCarts(_parent, args, context, _info) {
    const { limit, offset, customerId } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.READ,
    })

    let carts = {
      limit,
      offset,
    }

    const { customerCart, customerCartId } = await getCustomerCart(customerId, offset, user.employeeNumber)
    const { activeUserCarts, searchLimit } = await getActiveCustomerCarts(
      customerCart,
      limit,
      customerCartId,
      offset,
      user.employeeNumber,
    )

    if (customerCart?.length < 1 && (!activeUserCarts || activeUserCarts.results?.length < 1)) {
      return {
        ...carts,
        total: 0,
        items: [],
      }
    }

    const foundCarts = [...customerCart, ...(activeUserCarts?.results || [])].filter((n) => n)
    const customers = await getCartAssignedCustomers(foundCarts, searchLimit)

    if (!customers || customers.length < 1) {
      return {
        ...carts,
        total: 0,
        items: [],
      }
    }

    const cartsWithCustomer = foundCarts
      .map((cart) => {
        const customer = customers.find((c) => c.id === cart.customerId)
        if (!customer) return null

        return {
          ...cart,
          customer: customer || null,
        }
      })
      .filter((n) => n)

    let total = activeUserCarts.total || 0

    if (customerCartId.length > 0) {
      total += 1
    }

    return {
      ...carts,
      total,
      items: cartsWithCustomer,
    }
  },
}

/**
 * Retrieves customer cart if customerId is provided
 *
 * @param {?string} customerId
 * @param {?number} offset
 * @param {string} employeeNumber
 * @returns {Promise<{customerCart: any[], customerCartId: string}>}
 */
const getCustomerCart = async (customerId, offset, employeeNumber) => {
  let customerCart = []
  let customerCartId = ""

  if (customerId) {
    const activeCustomerCarts = await getActiveCustomerCart(customerId, employeeNumber)
    if (activeCustomerCarts?.length > 0) {
      customerCartId = activeCustomerCarts[0]?.id
      customerCart = !offset ? [activeCustomerCarts[0]] : []
    }
  }

  return { customerCart, customerCartId }
}

/**
 * Retrieves all active customer carts that have any custom line items
 *
 * @param {array} customerCart
 * @param {?number} limit
 * @param {string} customerCartId
 * @param {?number} offset
 * @param {string} employeeNumber
 * @returns {Promise<CartPagedQueryResponse>}
 */
const getActiveCustomerCarts = async (customerCart, limit, customerCartId, offset, employeeNumber) => {
  const searchLimit = customerCart && customerCart.length > 0 ? limit - 1 : limit
  const customerCartPredicate = customerCartId.length > 0 ? `and id not in ("${customerCartId}")` : ""
  const searchOffset = customerCartId.length > 0 && offset > 0 ? offset - 1 : offset || 0
  const config = AppSettings.ctpConfig()
  const ignoreBeforeQuery = config.cartIgnoreBeforeUtc
    ? `and (createdAt >= "${config.cartIgnoreBeforeUtc}" or lastModifiedAt >= "${config.cartIgnoreBeforeUtc}")`
    : ``

  const activeUserCarts = (
    await commerceApi
      .carts()
      .get({
        queryArgs: {
          where: `cartState="Active" and custom(fields(createdByEmployeeNumber="${employeeNumber}")) ${customerCartPredicate} and customLineItems is not empty ${ignoreBeforeQuery}`,
          offset: searchOffset,
          limit: searchLimit ?? 5,
        },
      })
      .execute()
  )?.body

  return { activeUserCarts, searchLimit }
}

/**
 * Retrieves all customers assigned to the cart
 *
 * @param {array} carts
 * @param {number} searchLimit
 * @returns {Promise<Customer[]>}
 */
const getCartAssignedCustomers = async (carts, searchLimit) => {
  const customerIds = carts.map((cart) => `"${cart.customerId}"`).join(", ")
  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          where: `id in (${customerIds})`,
          limit: searchLimit + 1 || 5,
        },
      })
      .execute()
  )?.body.results

  return customers
}
